import axios from "axios";
import { type Row } from "@tanstack/react-table";
import { Table } from "../../../../components/common/table/table";
import { type ColumnDef } from "@tanstack/react-table";
import { Text } from "../../../../components/common/typography";
import { EmptyState, type EmptyStateProps } from "../../../../components/common/empty-state";
import { usePaginationSortingUrlSync } from "../../hooks/usePaginationSortingUrlSync";
import type { PaginatedResponse } from "../../data";

// Type for hook options that can have params and SWR configuration
interface PaginationTableProps<T, TQuery> {
  // Hook that returns paginated data
  useDataHook: (query?: TQuery) => {
    data?: PaginatedResponse<T>;
    isLoading: boolean;
    error?: Error;
  };
  // Column definitions for the table
  columns: ColumnDef<T>[];
  // Base query parameters (search, filters) that get merged with pagination/sorting
  baseQuery?: TQuery;
  // Table configuration
  pageSize?: number;
  emptyMessage?: string;
  emptyState?: EmptyStateProps;
  // Optional tab bar component
  topComponent?: React.ReactNode;
  // Row click handler
  onRowClick?: (row: Row<T>) => void;
}

// Raw axios messages ("Request failed with status code 429") mean nothing to
// a buyer; say what happened and what to do instead.
function describeLoadError(error: Error): string {
  if (axios.isAxiosError(error) && error.response?.status === 429) {
    return "Too many requests in a short time. Please wait a moment and try again.";
  }
  return "We couldn't load this list. Please refresh the page or try again later.";
}

export function PaginationTable<T, TQuery>({
  useDataHook,
  columns,
  baseQuery = {} as TQuery,
  pageSize = 10,
  emptyMessage = "No data found",
  emptyState,
  topComponent,
  onRowClick,
}: PaginationTableProps<T, TQuery>) {
  const { pagination, setPagination, sorting, setSorting, sortingQuery } =
    usePaginationSortingUrlSync({
      defaultPageSize: pageSize,
    });

  // Build query by merging base query (search, filters) with pagination and sorting
  const hookQuery: TQuery = {
    ...baseQuery, // Search and filters from PaginationAppCard
    ...sortingQuery, // Sorting parameters
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  } as TQuery;

  const swrResponse = useDataHook(hookQuery);
  const response = swrResponse.data;
  // Use isLoading from hook, which now properly includes isValidating
  const isLoading = swrResponse.isLoading ?? (!response && !swrResponse.error);
  const error = swrResponse.error;
  const isEmpty = !isLoading && (!response?.data || response.data.length === 0);

  return (
    <div className="flex flex-col w-full min-w-0">
      {topComponent}

      <div className="flex-1 w-full min-w-0 pt-4">
        {error ? (
          <div className="flex items-center justify-center h-full">
            <Text variant="body" className="text-accentError">
              {describeLoadError(error)}
            </Text>
          </div>
        ) : isEmpty && emptyState ? (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <EmptyState {...emptyState} />
          </div>
        ) : (
          <Table
            data={response?.data || []}
            columns={columns}
            loading={isLoading}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
            pageCount={response?.meta?.pageCount || 0}
            emptyMessage={emptyMessage}
            onRowClick={onRowClick}
          />
        )}
      </div>
    </div>
  );
}
